import { z } from 'zod'

/**
 * Types and schemas for admin-related Firebase Functions
 */

// Zod schema for CreateCompanyRequest validation
export const CreateCompanyRequestSchema = z.object({
  companyData: z.object({
    name: z
      .string()
      .min(1, 'Company name is required')
      .max(100, 'Company name must be less than 100 characters'),
    identifier: z
      .string()
      .min(1, 'Company identifier is required')
      .max(50, 'Company identifier must be less than 50 characters')
      .regex(
        /^[a-z0-9_]+$/,
        'Company identifier must contain only lowercase letters, numbers, and underscores',
      ),
    logo: z.url('Logo must be a valid URL').optional().or(z.literal('')),
  }),
  adminUserData: z.object({
    firstName: z
      .string()
      .min(1, 'First name is required')
      .max(50, 'First name must be less than 50 characters'),
    lastName: z
      .string()
      .min(1, 'Last name is required')
      .max(50, 'Last name must be less than 50 characters'),
    email: z.email('Invalid email address'),
    login: z
      .string()
      .min(1, 'Login is required')
      .max(50, 'Login must be less than 50 characters'),
    phone: z
      .string()
      .regex(/^[\d\s\-()]+$/, 'Invalid phone number format')
      .optional()
      .or(z.literal('')),
  }),
})

export type CreateCompanyRequest = z.infer<typeof CreateCompanyRequestSchema>

export interface CreateCompanyResponse {
  companyId: string
  adminUserId: string
  message: string
}

// Create Company User schemas
export const CreateCompanyUserRequestSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.email('Invalid email address'),
  phone: z.string().optional(),
  role: z.enum(['mechanic', 'inspector', 'admin']),
  permissions: z
    .object({
      canSurvey: z.boolean().optional(),
      canLogMaintenance: z.boolean().optional(),
      canGenerateReports: z.boolean().optional(),
    })
    .optional(),
  workRouteIds: z.array(z.string()).optional(),
})

export type CreateCompanyUserRequest = z.infer<
  typeof CreateCompanyUserRequestSchema
>

export interface CreateCompanyUserResponse {
  success: boolean
  userId: string
  message: string
}

// Update Company User Name schemas (MCP-682)
export const UpdateCompanyUserNameRequestSchema = z.object({
  userId: z.string().min(1, 'User ID is required'),
  displayName: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters'),
  // Opt in to renaming despite a name collision (reports merge same-named
  // users); without it a collision is rejected with `duplicateWarning`.
  confirmDuplicate: z.boolean().optional(),
})

export type UpdateCompanyUserNameRequest = z.infer<
  typeof UpdateCompanyUserNameRequestSchema
>

export interface UpdateCompanyUserNameResponse {
  success: boolean
  message: string
  // True when a collision blocked the rename (no change made); retry with
  // confirmDuplicate to proceed.
  duplicateWarning?: boolean
}

// Create Company User as Super Admin schemas
export const CreateCompanyUserAsSuperAdminRequestSchema = z.object({
  companyId: z.string().min(1, 'Company ID is required'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.email('Invalid email address'),
  phone: z.string().optional(),
  role: z.enum(['mechanic', 'inspector', 'admin']),
  permissions: z
    .object({
      canSurvey: z.boolean().optional(),
      canLogMaintenance: z.boolean().optional(),
      canGenerateReports: z.boolean().optional(),
    })
    .optional(),
  workRouteIds: z.array(z.string()).optional(),
})

export type CreateCompanyUserAsSuperAdminRequest = z.infer<
  typeof CreateCompanyUserAsSuperAdminRequestSchema
>

// Customer Organization function schemas
export const CreateOrganizationRequestSchema = z.object({
  name: z.string().min(1, 'Organization name is required'),
  type: z.enum(['building_owner', 'property_manager', 'facilities_dept']),
  serviceCompanyId: z.string(),
  siteIds: z.array(z.string()).default([]),
  userIds: z.array(z.string()).default([]),
})

export const UpdateOrganizationRequestSchema = z.object({
  organizationId: z.string(),
  name: z.string().min(1).optional(),
  type: z
    .enum(['building_owner', 'property_manager', 'facilities_dept'])
    .optional(),
  active: z.boolean().optional(),
})

export const AssignSitesRequestSchema = z.object({
  organizationId: z.string(),
  siteIds: z.array(z.string()),
})

export const DeleteOrganizationRequestSchema = z.object({
  organizationId: z.string(),
})

export type CreateOrganizationRequest = z.infer<
  typeof CreateOrganizationRequestSchema
>
export type UpdateOrganizationRequest = z.infer<
  typeof UpdateOrganizationRequestSchema
>
export type AssignSitesRequest = z.infer<typeof AssignSitesRequestSchema>
export type DeleteOrganizationRequest = z.infer<
  typeof DeleteOrganizationRequestSchema
>

// Invite User to Organization schemas
export const InviteUserToOrganizationRequestSchema = z.object({
  organizationId: z.string(),
  email: z.string().email(),
  suggestedFirstName: z.string().optional(),
  suggestedLastName: z.string().optional(),
})

export type InviteUserToOrganizationRequest = z.infer<
  typeof InviteUserToOrganizationRequestSchema
>

export interface InviteUserToOrganizationResponse {
  success: boolean
  message: string
  userExists: boolean
  invitationId?: string
  userId?: string
}

// Impersonation function schemas
export const StartImpersonationRequestSchema = z.object({
  targetUserId: z.string().min(1),
})

export type StartImpersonationRequest = z.infer<
  typeof StartImpersonationRequestSchema
>

export interface StartImpersonationResponse {
  customToken: string
}

export const EndImpersonationRequestSchema = z.object({
  impersonatorUid: z.string().min(1).optional(),
})

export type EndImpersonationRequest = z.infer<
  typeof EndImpersonationRequestSchema
>

export interface EndImpersonationResponse {
  customToken: string
}
